export function arrayToLookup<T, K extends keyof T>(
  items: T[],
  lookUpKey: K
): Record<string, T> {
  const map: Record<string, T> = {};

  if (items && items.length > 0) {
    items.forEach((item) => {
      map[`${item[lookUpKey]}`] = item;
    });
  }

  return map;
}

export const arraysHaveIntersection = (arr1: unknown[], arr2: unknown[]) => {
  return arr1.some((item) => arr2.includes(item));
};

export const removeDuplicatesByKey = <T extends Record<string, unknown>>(
  arr: T[],
  key: keyof T
): T[] => {
  const uniqueMap = new Map<T[keyof T], T>();

  return arr.filter((item) => {
    if (uniqueMap.has(item[key])) {
      return false;
    }
    uniqueMap.set(item[key], item);
    return true;
  });
};

export function removeDuplicates(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

export function sortBy<T>(
  array: T[],
  key: keyof T,
  direction: "asc" | "desc" = "asc"
): T[] {
  return array.sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    // Handle null/undefined → always last
    const aIsNullish = aVal === null || aVal === undefined;
    const bIsNullish = bVal === null || bVal === undefined;

    if (aIsNullish && bIsNullish) return 0;
    if (aIsNullish) return 1;
    if (bIsNullish) return -1;

    if (direction === "asc") {
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
    } else {
      if (aVal > bVal) return -1;
      if (aVal < bVal) return 1;
    }
    return 0;
  });
}

export const getAverageForObjectArray = <T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
  filterNullValues: boolean = false
): number => {
  const filteredArray = filterNullValues
    ? array.filter((item) => item[key] !== null && item[key] !== undefined)
    : array;

  if (filteredArray.length === 0) {
    return 0;
  }

  return (
    filteredArray.reduce((acc, item) => acc + ((item[key] as number) ?? 0), 0) /
    filteredArray.length
  );
};

export const getMinMaxForObjectArray = <T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
  filterNullValues: boolean = false
): { min: number; max: number } => {
  const filteredArray = filterNullValues
    ? array.filter((item) => item[key] !== null && item[key] !== undefined)
    : array;

  if (filteredArray.length === 0) {
    return { min: 0, max: 0 };
  }

  return {
    min: Math.min(...filteredArray.map((item) => (item[key] as number) ?? 0)),
    max: Math.max(...filteredArray.map((item) => (item[key] as number) ?? 0)),
  };
};

export function removeNulls<T>(arr: (T | null)[]): T[] {
  return arr.filter((item): item is T => item !== null);
}

export function shuffleArray<T>(array: T[]): T[] {
  // Create a shallow copy to avoid mutating the original array
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
    const j = Math.floor(Math.random() * (i + 1));

    // Swap elements shuffled[i] and shuffled[j]
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

export function pickKeys<T, K extends keyof T>(
  items: T[],
  keys: K[]
): Pick<T, K>[] {
  return items.map((item) =>
    keys.reduce((accumulator, key) => {
      accumulator[key] = item[key];
      return accumulator;
    }, {} as Pick<T, K>)
  );
}
